import * as Phaser from "phaser"
import { InputManager } from "../core/input-mapper"
import { SCENE } from "../core/keys"
import { THEME } from "../core/theme"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { NEON, drawScanlines, neonTextShadow } from "../ui/neon-fx"
import { getWaveRuntime } from "./wave-runtime-api"

const FONT = THEME.fonts

interface BossCutsceneData {
  bossName: string
  bossTagline: string
  sectorName: string
}

export class BossCutsceneScene extends Phaser.Scene {
  private cutsceneData!: BossCutsceneData
  private dismissed = false
  private inputManager!: InputManager

  constructor() {
    super({ key: SCENE.BOSS_CUTSCENE })
  }

  init(data: BossCutsceneData): void {
    this.cutsceneData = data
    this.dismissed = false
  }

  create(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    // Dark overlay — fades in
    const bg = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
    this.inputManager = new InputManager(this, {
      onDown: (action) => {
        if (action === "CONFIRM" || action === "FLAP" || action === "ADVANCE") {
          this.dismiss()
        }
      },
    })
    this.inputManager.bindScenePointer(
      "pointerdown",
      (p: Phaser.Input.Pointer) => {
        p.event.stopPropagation()
      },
    )
    this.events.once("shutdown", () => {
      this.inputManager.destroy()
    })
    this.tweens.add({ targets: bg, alpha: 0.94, duration: 250 })

    // Scanlines
    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.05, 3).setDepth(5)

    // Red warning bars
    const topBar = this.add
      .rectangle(cx, 52, GAME_WIDTH, 46, NEON.red, 0)
      .setDepth(6)
    const botBar = this.add
      .rectangle(cx, GAME_HEIGHT - 52, GAME_WIDTH, 46, NEON.red, 0)
      .setDepth(6)
    this.tweens.add({
      targets: [topBar, botBar],
      alpha: 1,
      duration: 180,
      delay: 200,
    })

    // WARNING text
    const warningText = this.add
      .text(cx, 52, "! WARNING !", {
        fontFamily: FONT.mono,
        fontSize: "20px",
        color: "#000000",
        letterSpacing: 10,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(10)

    const sectorText = this.add
      .text(cx, GAME_HEIGHT - 52, this.cutsceneData.sectorName.toUpperCase(), {
        fontFamily: FONT.mono,
        fontSize: "14px",
        color: "#000000",
        letterSpacing: 8,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(10)

    this.tweens.add({
      targets: [warningText, sectorText],
      alpha: 1,
      duration: 180,
      delay: 350,
    })

    // Blink warning text
    this.time.delayedCall(600, () => {
      this.tweens.add({
        targets: warningText,
        alpha: 0,
        duration: 100,
        yoyo: true,
        repeat: 5,
      })
    })

    // Horizontal accent lines
    const lineTop = this.add
      .rectangle(cx, 96, GAME_WIDTH, 2, NEON.red, 0)
      .setDepth(6)
    const lineBot = this.add
      .rectangle(cx, GAME_HEIGHT - 96, GAME_WIDTH, 2, NEON.red, 0)
      .setDepth(6)
    this.tweens.add({
      targets: [lineTop, lineBot],
      alpha: 0.7,
      duration: 300,
      delay: 300,
    })

    // APEX THREAT label
    this.add
      .text(cx, cy - 100, "— APEX THREAT DETECTED —", {
        fontFamily: FONT.mono,
        fontSize: "13px",
        color: "#ff4444",
        letterSpacing: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(10)
      .setData("tween-target", true)

    const labelObj = this.children.list[
      this.children.list.length - 1
    ] as Phaser.GameObjects.Text
    this.tweens.add({
      targets: labelObj,
      alpha: 0.85,
      duration: 250,
      delay: 500,
    })

    // Boss name — revealed by typewriter after camera shake
    const bossNameObj = this.add
      .text(cx, cy - 24, "", {
        fontFamily: FONT.mono,
        fontSize: "54px",
        color: "#ffffff",
        stroke: "#ff2222",
        strokeThickness: 2,
        shadow: neonTextShadow("#ff222288", 18),
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setDepth(10)

    // Boss tagline — revealed after name
    const taglineObj = this.add
      .text(cx, cy + 52, "", {
        fontFamily: FONT.mono,
        fontSize: "15px",
        color: "#ff6666",
        letterSpacing: 5,
      })
      .setOrigin(0.5)
      .setDepth(10)

    // HP bar reveal
    const hpBg = this.add
      .rectangle(cx, cy + 110, 500, 12, 0x330000, 0)
      .setDepth(10)
    const hpFill = this.add
      .rectangle(cx - 250, cy + 110, 0, 8, NEON.red, 0)
      .setOrigin(0, 0.5)
      .setDepth(11)
    const hpLabel = this.add
      .text(cx, cy + 132, "THREAT LEVEL ■■■■■■■■■■ CRITICAL", {
        fontFamily: FONT.mono,
        fontSize: "11px",
        color: "#ff4444",
        letterSpacing: 3,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(11)

    // Engage countdown text
    const countdownObj = this.add
      .text(cx, cy + 180, "", {
        fontFamily: FONT.mono,
        fontSize: "30px",
        color: "#ffffff",
        stroke: "#ff0000",
        strokeThickness: 1,
        letterSpacing: 10,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(10)

    // ── Timeline ────────────────────────────────────────────────
    // t=0ms: bg + bars fade in
    // t=400ms: camera shake + flash
    // t=700ms: boss name typewriter
    // t=name_done + 200ms: tagline typewriter
    // t=1800ms: HP bar fills
    // t=2800ms: ENGAGE countdown

    this.time.delayedCall(400, () => {
      this.cameras.main.shake(600, 0.014)
      this.cameras.main.flash(0.6, 255, 0, 0)
    })

    this.time.delayedCall(700, () => {
      this.typewriterReveal(bossNameObj, this.cutsceneData.bossName, 55, () => {
        this.time.delayedCall(200, () => {
          this.typewriterReveal(taglineObj, this.cutsceneData.bossTagline, 35)
        })
      })
    })

    this.time.delayedCall(1800, () => {
      hpBg.setAlpha(1)
      this.tweens.add({
        targets: hpFill,
        width: 500,
        alpha: 1,
        duration: 700,
        ease: "Power2.easeOut",
      })
      this.tweens.add({
        targets: hpLabel,
        alpha: 0.8,
        duration: 350,
        delay: 500,
      })
    })

    this.time.delayedCall(2800, () => {
      this.runEngageCountdown(countdownObj)
    })

    // Safety timeout
    this.time.delayedCall(6000, () => this.dismiss())
  }

  private typewriterReveal(
    obj: Phaser.GameObjects.Text,
    fullText: string,
    msPerChar: number,
    onComplete?: () => void,
  ): void {
    let i = 0
    obj.setText("")
    const ev = this.time.addEvent({
      delay: msPerChar,
      repeat: fullText.length - 1,
      callback: () => {
        i++
        obj.setText(fullText.slice(0, i))
        if (i >= fullText.length) {
          ev.destroy()
          onComplete?.()
        }
      },
    })
  }

  private runEngageCountdown(obj: Phaser.GameObjects.Text): void {
    const steps = ["3", "2", "1", "ENGAGE"]
    let i = 0
    const tick = () => {
      if (this.dismissed) return
      if (i >= steps.length) {
        this.dismiss()
        return
      }
      obj.setText(steps[i]).setAlpha(1)
      this.tweens.add({
        targets: obj,
        alpha: 0,
        duration: 550,
        ease: "Power2",
      })
      this.cameras.main.shake(80, 0.006)
      i++
      this.time.delayedCall(700, tick)
    }
    tick()
  }

  private dismiss(): void {
    if (this.dismissed) return
    this.dismissed = true

    this.tweens.killAll()
    this.tweens.add({
      targets: this.children.list,
      alpha: 0,
      duration: 280,
      ease: "Power2",
      onComplete: () => {
        this.scene.stop()
        const waveRuntime = getWaveRuntime(this)
        ;(
          waveRuntime as { finishBossCutscene?: () => void }
        )?.finishBossCutscene?.()
      },
    })
  }
}
